import React, { useState } from "react";
import { Link } from "react-router-dom";
import { GiftIcon, LoadingIcon } from "../assets/icons/Icons";
import CustomTooltip from "./CustomTooltip";
import { useTranslation } from "react-i18next";
import usePrint from "../hooks/usePrint";
import { getGiftReceipt } from "../api/sell-orders/getGiftReceipt";
import { GetSellOrder } from "../api/sell-orders/getSellOrder";
import Popup from "./Popup";
import FormHeader from "./form/Header";
import { CentsToDollars } from "../utils/formatPrice";
import { GetBatchSellOrder } from "../api/batch-order/getBatchSellOrder";

function GiftReceipt({ order }) {
  const { t } = useTranslation();
  const [loading, setloading] = useState(false);
  const [fullorder, setFullorder] = useState(null);
  const [showPopup, setshowPopup] = useState(false);
  const [selectedCards, setselectedCards] = useState([]);
  const { handlePrint: handlePrintGiftReceipt } = usePrint(getGiftReceipt);
  const fetchOrder = async (id, type) => {
    setloading(true);
    try {
      const FetchOrder = type === "batch" ? GetBatchSellOrder : GetSellOrder;
      const { data } = await FetchOrder({ id });
      setFullorder(data);
      if (data.cards.length > 1) {
        setshowPopup(true);
      } else {
        handlePrintGiftReceipt({
          requestParams: { id: data.id, type: type },
          name: `order-${data.id}`,
          width: "",
          height: "",
          download: false,
        });
      }
    } catch (err) {
      console.log("Error fetching order: " + err);
    }
    setloading(false);
  };
  return (
    <>
      {showPopup && fullorder && (
        <Popup
          size="large"
          withClose
          withOverlay
          onClose={() => setshowPopup(false)}
        >
          <FormHeader title={`Gift Receipts`}></FormHeader>
          <div className="mt-5">
            <h5>Order ID: {fullorder.id}</h5>
            <h5>Cards</h5>
            <table className="mb-3">
              <thead>
                <tr>
                  <th>
                    <div className="checkbox-small">
                      <input
                        type="checkbox"
                        name={"selectAll"}
                        value={"selectAll"}
                        id={"selectAll"}
                        checked={
                          selectedCards.length === fullorder.cards.length
                        }
                        onChange={() =>
                          setselectedCards(
                            selectedCards.length === fullorder.cards.length
                              ? []
                              : fullorder.cards.map(
                                  (card) => card.processor_reference
                                )
                          )
                        }
                      />
                      <label htmlFor={"selectAll"}></label>
                    </div>
                  </th>
                  <th>{t("PRN Number")}</th>
                  <th>{t("Amount")}</th>
                  <th className="text-center">{t("Print Receipt")}</th>
                </tr>
              </thead>
              <tbody>
                {fullorder?.cards?.map((card) => (
                  <tr key={card.processor_reference}>
                    <td>
                      <div className="checkbox-small">
                        <input
                          type="checkbox"
                          name={"prns"}
                          value={card.processor_reference}
                          id={card.processor_reference}
                          checked={selectedCards.includes(
                            card.processor_reference
                          )}
                          onChange={() =>
                            setselectedCards((prev) =>
                              prev.includes(card.processor_reference)
                                ? prev.filter(
                                    (prn) => prn !== card.processor_reference
                                  )
                                : [...prev, card.processor_reference]
                            )
                          }
                        />
                        <label htmlFor={card.processor_reference}></label>
                      </div>
                    </td>
                    <td>{card.processor_reference}</td>
                    <td>{CentsToDollars(card.load_amount)}</td>
                    <td className="text-center">
                      <PrintGiftIndividualCard
                        order={order}
                        prn={card.processor_reference}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-right d-flex gap-2 justify-content-between align-items-center flex-wrap mb-2">
              <PrintGiftAllIndividually
                order={fullorder}
                ids={selectedCards}
                label={t("Print Selected Cards")}
                disabled={selectedCards.length === 0}
              />
              <PrintGiftAllIndividually
                order={fullorder}
                ids={fullorder?.cards?.map((card) => card.processor_reference)}
              />
            </div>
          </div>
        </Popup>
      )}
      <Link
        to="#"
        onClick={(e) => {
          e.preventDefault();
          !loading && fetchOrder(order.id, order.type);
        }}
        disabled={loading}
      >
        {loading ? (
          <CustomTooltip
            icon={<LoadingIcon />}
            message={t("Fetching Gift Receipt")}
          />
        ) : (
          <CustomTooltip
            icon={<GiftIcon />}
            message={t("Print Gift Receipt")}
          />
        )}
      </Link>
    </>
  );
}

export default GiftReceipt;

export const PrintGiftIndividualCard = ({ order, prn }) => {
  const { t } = useTranslation();
  const [loading, setloading] = useState(false);
  const { handlePrint: handlePrintGiftReceipt } = usePrint(getGiftReceipt);
  return (
    <>
      <Link
        to="#"
        onClick={async (e) => {
          e.preventDefault();
          setloading(true);
          await handlePrintGiftReceipt({
            requestParams: { id: order.id, prn: prn, type: order.type },
            name: `order-${order.id}-${prn}`,
            width: "",
            height: "",
            download: false,
          });
          setloading(false);
        }}
        disabled={loading}
      >
        {loading ? (
          <CustomTooltip
            icon={<LoadingIcon />}
            message={t("Fetching Gift Receipt")}
          />
        ) : (
          <CustomTooltip
            icon={<GiftIcon />}
            message={t("Print Gift Receipt")}
          />
        )}
      </Link>
    </>
  );
};

export const PrintGiftAllIndividually = ({ order, ids, label, disabled }) => {
  const { t } = useTranslation();
  const [loading, setloading] = useState(false);
  const { handlePrint: handlePrintGiftReceipt } = usePrint(getGiftReceipt);
  return (
    <button
      className="btn btn-primary"
      onClick={async () => {
        setloading(true);
        await handlePrintGiftReceipt({
          requestParams: { id: order.id, ids: ids, type: order.type },
          name: `order-${order.id}`,
          width: "",
          height: "",
          download: false,
        });
        setloading(false);
      }}
      disabled={loading || disabled}
    >
      {loading ? <LoadingIcon /> : t(label || "Print All Individually")}
    </button>
  );
};
